import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useLoading } from '../../../contexts/LoadingContext';
import { containsProfanity } from '../../../utils/profanityUtils';
import AvatarSelector from './AvatarSelector';
import ErrorMessage from '../../common/ErrorMessage';
import LoadingButton from '../../common/LoadingButton';

const ProfileSetupStep = ({ onSubmit, initialData = {}, email }) => {
    const { register } = useAuth();
    const { startLoading, stopLoading } = useLoading();
    const [formData, setFormData] = useState({
        username: '',
        password: '',
        avatar_color: '#377AD3',
        jersey_number: '00'
    });
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        try {
            const username = formData.username.trim()
                .replace(/[^a-zA-Z0-9_-]/g, '') // Only allow alphanumeric, underscore, hyphen
                .slice(0, 30); // Enforce max length
            
            if (!username) {
                setError('Username is required');
                return;
            }

            if (username.length < 3) {
                setError('Username must be at least 3 characters long');
                return;
            }

            if (!/^[a-zA-Z0-9_-]+$/.test(username)) {
                setError('Username can only contain letters, numbers, underscores, and hyphens');
                return;
            }

            if (containsProfanity(username)) {
                setError('Username contains inappropriate content');
                return;
            }

            // Password validation only if password field is shown (i.e., hasn't been set yet)
            if (!initialData?.has_set_password && formData.password) {
                if (formData.password.length < 8) {
                    setError('Password must be at least 8 characters long');
                    return;
                }
            }

            startLoading('profileSetup');
            const profileData = {
                username: formData.username,
                avatar_color: formData.avatar_color,
                jersey_number: formData.jersey_number,
                onError: (error) => setError(error)
            };

            // Only include password if it hasn't been set yet
            if (!initialData?.has_set_password && formData.password) {
                profileData.password = formData.password;
            }

            await onSubmit(profileData);
        } catch (error) {
            const message =
                error.response?.data?.detail ||
                error.message ||
                'Profile setup failed. Please try again.';
            setError(message);
        } finally {
            stopLoading('profileSetup');
        }
    };

    const handleCustomizationUpdate = ({ color, jerseyNumber }) => {
        setFormData((prev) => ({
            ...prev,
            avatar_color: color ?? prev.avatar_color,
            jersey_number: jerseyNumber ?? prev.jersey_number,
        }));
    };

    return (
        <div className="max-w-md w-full">
            <div className="text-center">
                <h1>Complete your profile</h1>
                <p className="mt-4">You're signing up as {email}.</p>
            </div>
            <form className="space-y-4" onSubmit={handleSubmit}>
                <div className="mt-4">
                    <div>
                        <label htmlFor="username" className="sr-only">
                            Display Name
                        </label>
                        <input
                            id="username"
                            name="username"
                            type="text"
                            required
                            maxLength={11}
                            className={`appearance-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm ${!initialData?.has_set_password ? 'rounded-t-md border-b-0' : 'rounded-md'}`}
                            placeholder="Username"
                            autoComplete="username"
                            spellCheck="false"
                            enterKeyHint="next"
                            aria-label="Username"
                            value={formData.username}
                            onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                        />
                    </div>
                    {initialData?.has_set_password === false && (
                        <div>
                            <label htmlFor="password" className="sr-only">
                                Password
                            </label>
                            <input
                                id="password"
                                name="password"
                                type="password"
                                required
                                className="appearance-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                                placeholder="Choose a password"
                                autoComplete="new-password"
                                enterKeyHint="done"
                                aria-label="New password"
                                value={formData.password}
                                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                            />
                        </div>
                    )}
                </div>

                <AvatarSelector
                    onSelectAvatar={handleCustomizationUpdate}
                    username={formData.username}
                />
                <ErrorMessage error={error} />
                <div className="space-y-2">
                    <LoadingButton
                        type="submit"
                        className="w-full btn-primary"
                        loadingKey="profileSetup"
                    >
                        Let&apos;s Play!
                    </LoadingButton>
                    <div className="space-y-1">
                        <p className="text-sm text-center">You're signing up as {email}.</p>
                        <p className="text-sm text-center">
                            Wrong account? <button onClick={() => window.location.href = '/login'} className="text-popashot-blue-light hover:text-popashot-blue">Sign in</button> instead.
                        </p>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default ProfileSetupStep;
